import React, { useState, useMemo } from 'react';
import {
    Button,
    Header as HeaderDS,
    Icon,
    useIsBreakpointUp,
} from '@soluto-design/react';
import Link, { LinkProps } from '@soluto-design/link';
import cls from '@soluto-design/styles/cls';

import MegaMenu, { MegaMenuProps } from './components/MegaMenu';

interface MenuItem {
    items?: LinkProps[];
    label?: string;
    href: string;
    variant?: 'primary' | 'secondary';
    target?: '_blank' | '_self' | '_parent' | '_top';
    children?: string;
    onClick?: (ev: React.MouseEvent<HTMLAnchorElement>) => void;
    leftIcon?: string;
}

export interface HeaderProps extends Omit<MegaMenuProps, 'buttons'> {
    leftItems: MenuItem[];
    rightItems: MenuItem[];
    mobileItems: MenuItem[];
    mobileBottomItems: MenuItem[];
    withBanner?: boolean;
}

type HeaderContextType = {
    isOpen: boolean;
    columnsOpen: number[][];
    handleOpen: () => void;
    toggleColumn: (column: number, index: number) => void;
};

const HeaderContext = React.createContext<HeaderContextType>({
    isOpen: false,
    columnsOpen: [],
    handleOpen: () => {},
    toggleColumn: () => {},
});

function HeaderProvider({ children }: { children: React.ReactNode }) {
    const [headerState, setHeaderState] = useState<{
        isOpen: HeaderContextType['isOpen'];
        columnsOpen: HeaderContextType['columnsOpen'];
    }>({
        isOpen: false,
        columnsOpen: [],
    });

    const handleOpen = () => {
        setHeaderState({ ...headerState, isOpen: !headerState.isOpen });
    };

    const toggleColumn = (column: number, index: number) => {
        // Set column and add index to the array
        const columns = [...headerState.columnsOpen];

        if (!Array.isArray(columns[column])) {
            columns[column] = [];
        }

        if (columns[column].includes(index)) {
            columns[column] = columns[column].filter((i) => i !== index);
        } else {
            columns[column].push(index);
        }

        setHeaderState({ ...headerState, columnsOpen: columns });
    };

    return (
        <HeaderContext.Provider
            value={{
                isOpen: headerState.isOpen,
                columnsOpen: headerState.columnsOpen,
                handleOpen,
                toggleColumn,
            }}>
            {children}
        </HeaderContext.Provider>
    );
}

export const useHeaderContext = () => React.useContext(HeaderContext);

const Header = ({
    leftItems,
    rightItems,
    mobileItems,
    mobileBottomItems,
    highlightedPost,
    menu,
    highlightedPostTitle,
    video,
    banner,
    withBanner,
}: HeaderProps) => {
    const isDesktop = useIsBreakpointUp('lg');

    const [menuItems, menuButtons] = useMemo(() => {
        if (isDesktop) {
            return [menu, rightItems];
        }

        return [
            [
                {
                    items: mobileItems,
                },
            ],
            mobileBottomItems,
        ];
    }, [isDesktop, mobileItems, mobileBottomItems, menu, rightItems]);

    return (
        <HeaderProvider>
            <HeaderContext.Consumer>
                {({ isOpen, handleOpen }) => (
                    <HeaderDS
                        sticky
                        variant="default"
                        classes={{
                            root: cls([
                                'mt-16 container inset-x-0',
                                withBanner &&
                                    'transition-transform duration-300 translate-y-[var(--header-alert-height)] delay-[var(--header-alert-delay)]',
                            ]),
                            wrapper: cls('flex-1 !items-start '),
                            children: cls(
                                'flex-1 !items-start overflow-hidden',
                            ),
                        }}>
                        <div
                            style={
                                // Set max header height to variable vh - header alert height - 32px
                                {
                                    '--header-max-h':
                                        'calc((var(--vh, 1vh) * 100) - var(--header-alert-height,0px) - 32px)',
                                } as React.CSSProperties
                            }
                            className={cls(
                                'transition-all duration-300 ease-in-out flex flex-col rounded-[36px] relative flex-1 px-md overflow-hidden backdrop-blur',
                                'max-h-[var(--header-max-h)]',
                                isOpen
                                    ? 'bg-background-primary theme-dark'
                                    : 'bg-background-primary-opacity theme-light',
                            )}>
                            <div className="flex items-center py-16">
                                <div>
                                    <Link href="/">
                                        <Icon
                                            icon="brand-logo"
                                            className="h-[26px] md:h-28 xl:h-32"
                                        />
                                    </Link>
                                </div>
                                <div className="flex-1 gap-gutter items-center justify-center hidden lg:flex">
                                    {leftItems?.length > 0 &&
                                        leftItems.map((item, index) => (
                                            <Button
                                                key={`left-item-${item?.label}`}
                                                href={item?.href}
                                                target={item?.target}
                                                onClick={item?.onClick}
                                                classes={{
                                                    active: 'text-text-secondary',
                                                }}
                                                variant="text"
                                                size="text">
                                                {item?.label ?? item?.children}
                                            </Button>
                                        ))}
                                </div>
                                <div className="gap-gutter flex ml-auto">
                                    <div className="gap-gutter hidden md:flex">
                                        {rightItems?.length > 0 &&
                                            rightItems.map((item, index) => (
                                                <Button
                                                    key={`right-item-${item?.label}`}
                                                    href={item?.href}
                                                    target={item?.target}
                                                    onClick={item?.onClick}
                                                    variant={
                                                        item?.variant ??
                                                        'primary'
                                                    }
                                                    leftIcon={
                                                        item?.leftIcon ?? null
                                                    }
                                                    size="large">
                                                    {item?.label ??
                                                        item?.children}
                                                </Button>
                                            ))}
                                    </div>
                                    <Button
                                        variant="secondary"
                                        size="icon_large"
                                        leftIcon="menu"
                                        className="[&>svg]:h-16 [&>svg]:w-16"
                                        onClick={handleOpen}
                                        component="button"
                                    />
                                </div>
                            </div>

                            <div
                                className={cls(
                                    'transition-all duration-300 ease-in-out grid grid-rows-[0fr]',
                                    isOpen && 'grid-rows-[1fr] overflow-auto',
                                )}>
                                <div className="overflow-hidden">
                                    <MegaMenu
                                        buttons={menuButtons}
                                        menu={menuItems}
                                        highlightedPost={highlightedPost}
                                        highlightedPostTitle={
                                            highlightedPostTitle
                                        }
                                        video={video}
                                        banner={banner}
                                    />
                                </div>
                            </div>
                        </div>
                    </HeaderDS>
                )}
            </HeaderContext.Consumer>
        </HeaderProvider>
    );
};

export default Header;
